import * as C from '../common/constants';
import Dialog from '@mui/material/Dialog';
import MuiDialogContent from '@mui/material/DialogContent';
import { COLORS, FONTS, FONT_WEIGHT } from '../common/theme';
import { ReactComponent as CloseIcon } from '../assets/svg/cancel_circle.svg';
import { getText } from '../common/utils/helpers';
import { useSelector, useDispatch } from 'react-redux';
import clsx from 'clsx';
import { StyleRules, createStyles, makeStyles } from '@mui/styles';
import { storeAuthData } from '../common/app/actions';
import Button from './Button';
import {
  getAuth,
  signInWithPopup,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import { db } from '../config';
import {
  getFirestore,
  collection,
  addDoc,
  doc,
  setDoc,
  getDoc,
} from 'firebase/firestore';

interface Props {
  onClose: () => void;
}

const useStyles = makeStyles(
  (): StyleRules =>
    createStyles({
      dialogContainerStyle: {
        '& > div > div': {
          maxWidth: `500px`,
          width: '100%',
          background: COLORS.white,
        },
      },
      closeButtonStyle: {
        position: 'absolute',
        right: 20, // theme.spacing(2),
        top: 20, // theme.spacing(2),
        fill: COLORS.textGray,
        width: '30px',
        height: '30px',
        pointerEvents: 'auto',
        zIndex: 100,
        cursor: 'pointer',
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': {
            opacity: 0.7,
            transition: '0.5s ease',
          },
        },
      },
      dialogContentStyle: {
        // margin: '16px',
        color: COLORS.textBlack,
      },
      titleStyle: {
        width: '100%',
        // fontFamily: FONTS.garamond,
        // fontWeight: FONT_WEIGHT.medium,
        // color: COLORS.textBlack,
        fontWeight: FONT_WEIGHT.medium,
        fontFamily: FONTS.alegreya,
        color: COLORS.appBlue,
        textAlign: 'center',
        // textTransform: 'uppercase',
        fontSize: '20px',
        margin: '5px',
      },
      spanStyle: {
        marginBottom: '20px',
      },
      buttonContainerStyle: {
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
      },
      buttonWrapperStyle: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '300px',
      },
      inputContainerStyle: {
        position: 'relative',
        border: 'gray solid 1px',
        background: COLORS.white,
        borderRadius: '8px',
        boxSizing: 'border-box',
        padding: '10px 16px',
        marginBottom: '10px',
      },
      inputContainerTextStyle: {
        maxHeight: '40px',
        display: 'flex',
        alignItems: 'center',
      },
      textStyle: {
        width: '100%',
        background: 'transparent',
        resize: 'none',
        overflow: 'hidden',
        boxShadow: 'none !important',
        fontFamily: FONTS.alegreya,
        fontSize: '16px',
        lineHeight: '22px',
        border: 'none',
      },
    })
);

const useArrayRef = (): any => {
  const refs = [];
  return [refs, (el) => el && refs.push(el)];
};

const LoginDialog = ({ onClose }: Props) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  // const lng = useSelector((state: any) => state.app.lng);

  // const [provider, setProvider] = useState(null);
  // const [authData, setAuthData] = useState<any>();

  const [elements, refs] = useArrayRef();

  const auth = getAuth();

  const isMobile = window.innerWidth < 900;

  // console.log('>>>>', provider, profile);

  const doLogin = (formData) => {
    signInWithEmailAndPassword(auth, formData?.email, formData?.password)
      .then((userCredential) => {
        const user = userCredential.user;

        dispatch(storeAuthData(user));
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const createAccountOrLogin = (formData) => {
    createUserWithEmailAndPassword(auth, formData?.email, formData?.password)
      .then((userCredential) => {
        // Signed in
        const user = userCredential.user;

        // console.log('>>>>', userCredential);
        dispatch(storeAuthData(user));

        //store e-mail at the account creation
        const userRef = doc(db, 'users', user?.uid);
        setDoc(userRef, {
          profile: { email: user.email },
        });
      })
      .catch((error) => {
        if (error.code === 'auth/email-already-in-use') {
          doLogin(formData);
        }
      });
  };

  const onLogin = async () => {
    // const newProvider = new FacebookAuthProvider();
    // newProvider.addScope('id');
    // // 'id,first_name,last_name,picture,email,gender,user_birthday'
    // newProvider.setCustomParameters({
    //   display: 'popup',
    // });
    // setProvider(newProvider);
    const formData: any = {};

    (elements as any[]).forEach((elementItem) => {
      // console.log('>>>>111', elementItem);
      const elementKey = elementItem?.getAttribute('data-name');
      // const inputType = elementItem?.getAttribute('data-type');

      formData[elementKey] = elementItem?.value;
    });

    // await checkAuth();
    createAccountOrLogin(formData);
  };

  return (
    <div>
      <Dialog
        fullScreen={isMobile}
        open={true}
        onClose={onClose}
        className={classes.dialogContainerStyle}
      >
        <CloseIcon className={classes.closeButtonStyle} onClick={onClose} />
        <MuiDialogContent className={classes.dialogContentStyle}>
          <div className={classes.titleStyle}>{'Lūdzu autorizējies'}</div>

          <div className={classes.spanStyle} />

          <div className={classes.buttonContainerStyle}>
            <div className={classes.buttonWrapperStyle}>
              <div
                className={clsx(
                  classes.inputContainerStyle,
                  classes.inputContainerTextStyle
                )}
              >
                <input
                  className={classes.textStyle}
                  placeholder={'Epasts'}
                  maxLength={380}
                  ref={refs}
                  data-name={'email'}
                  type="email"
                />
              </div>

              <div
                className={clsx(
                  classes.inputContainerStyle,
                  classes.inputContainerTextStyle
                )}
              >
                <input
                  className={classes.textStyle}
                  placeholder={'Parole'}
                  maxLength={380}
                  ref={refs}
                  data-name={'password'}
                  type="password"
                />
              </div>

              <Button
                onClick={onLogin}
                isProgress={false}
                text={'AUTORIZĒTIES'}
                disabled={false}
                className={classes.loginButtonStyle}
              />

              <div className={classes.spanStyle} />

              <div className={classes.titleStyle}>{'vai'}</div>

              <div className={classes.spanStyle} />

              <Button
                onClick={onClose}
                isProgress={false}
                text={'TURPINĀT BEZ AUTORIZĀCIJAS'}
                disabled={false}
                className={classes.saveButtonStyle}
              />
            </div>
          </div>
        </MuiDialogContent>
      </Dialog>
    </div>
  );
};

export default LoginDialog;
